import React, { useMemo, useState } from "react";
import { auth } from "../lib/api";
import { toast } from "sonner";
import { ShieldCheck, Check, ChevronDown, ChevronUp } from "lucide-react";

const MIN_LEN = 8;

export default function ChangePasswordPanel() {
  const [open, setOpen] = useState(false);
  const [cur, setCur] = useState("");
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);

  const rules = useMemo(
    () => [
      { key: "len", label: `At least ${MIN_LEN} characters`, ok: pw1.length >= MIN_LEN },
      { key: "match", label: "Both passwords match", ok: pw1.length > 0 && pw1 === pw2 },
      { key: "diff", label: "Different from current password", ok: pw1.length > 0 && pw1 !== cur },
    ],
    [cur, pw1, pw2],
  );
  const allValid = cur.length > 0 && rules.every((r) => r.ok);

  const submit = async (e) => {
    e.preventDefault();
    if (!allValid) return;
    setBusy(true);
    try {
      await auth.changePassword(cur, pw1);
      toast.success("Your password has been updated.");
      setCur("");
      setPw1("");
      setPw2("");
      setOpen(false);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't update password.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="profile-security-panel"
      className="rounded-3xl border border-[#DCE5DB] bg-white shadow-[0_4px_12px_rgba(0,0,0,0.04)] overflow-hidden"
    >
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="profile-change-password-toggle"
        className="w-full flex items-center justify-between px-6 py-4 hover:bg-[#EEF3EC]/50 transition"
      >
        <div className="flex items-center gap-2.5">
          <span className="w-8 h-8 rounded-2xl bg-[#E1ECDD] text-[#4F6F52] flex items-center justify-center">
            <ShieldCheck size={15} strokeWidth={2} />
          </span>
          <div className="text-left">
            <div className="font-medium text-[#2E2E2E]">Account &amp; Security</div>
            <div className="text-xs text-[#6B6B6B]">Change your password</div>
          </div>
        </div>
        {open ? (
          <ChevronUp size={16} className="text-[#6B6B6B]" />
        ) : (
          <ChevronDown size={16} className="text-[#6B6B6B]" />
        )}
      </button>

      {open && (
        <form onSubmit={submit} className="px-6 pb-6 pt-1 space-y-3.5 border-t border-[#EEF3EC]">
          <label className="block mt-2">
            <span className="text-sm text-[#6B6B6B] mb-1.5 block">Current password</span>
            <input
              type="password"
              required
              data-testid="cp-current"
              value={cur}
              onChange={(e) => setCur(e.target.value)}
              className="w-full bg-[#F9FAF7] border border-[#DCE5DB] rounded-2xl px-4 py-3 focus:outline-none focus:border-[#4F6F52] focus:ring-2 focus:ring-[#4F6F52]/15 transition"
            />
          </label>
          <label className="block">
            <span className="text-sm text-[#6B6B6B] mb-1.5 block">New password</span>
            <input
              type="password"
              required
              data-testid="cp-new"
              value={pw1}
              onChange={(e) => setPw1(e.target.value)}
              placeholder={`At least ${MIN_LEN} characters`}
              className="w-full bg-[#F9FAF7] border border-[#DCE5DB] rounded-2xl px-4 py-3 focus:outline-none focus:border-[#4F6F52] focus:ring-2 focus:ring-[#4F6F52]/15 transition"
            />
          </label>
          <label className="block">
            <span className="text-sm text-[#6B6B6B] mb-1.5 block">Confirm new password</span>
            <input
              type="password"
              required
              data-testid="cp-confirm"
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
              className="w-full bg-[#F9FAF7] border border-[#DCE5DB] rounded-2xl px-4 py-3 focus:outline-none focus:border-[#4F6F52] focus:ring-2 focus:ring-[#4F6F52]/15 transition"
            />
          </label>
          <ul data-testid="cp-rules" className="text-xs space-y-1">
            {rules.map((r) => (
              <li
                key={r.key}
                className={`flex items-center gap-2 ${r.ok ? "text-[#3F5A42]" : "text-[#6B6B6B]"}`}
              >
                <span className={`inline-flex w-4 h-4 rounded-full items-center justify-center ${r.ok ? "bg-[#4F6F52] text-white" : "bg-[#EEF3EC] text-[#6B6B6B]"}`}>
                  {r.ok ? <Check size={10} strokeWidth={3} /> : <span className="w-1 h-1 rounded-full bg-current" />}
                </span>
                {r.label}
              </li>
            ))}
          </ul>
          <button
            type="submit"
            disabled={!allValid || busy}
            data-testid="cp-submit"
            className="w-full bg-[#4F6F52] hover:bg-[#3F5A42] disabled:opacity-60 text-white rounded-full py-3 font-semibold transition"
          >
            {busy ? "Updating..." : "Update password"}
          </button>
        </form>
      )}
    </div>
  );
}
